import type { SurveyAttribute, SurveyHighLowAnalysisScores } from '@engagement-web-app/common';
import {
    BarController,
    BarElement,
    CategoryScale,
    type ChartData,
    Chart as ChartJS,
    type ChartOptions,
    Legend,
    LinearScale,
    type Plugin,
    Title,
    Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import { ErrorOverlay } from '@/components/ErrorOverlay';
import { Flex } from '@/components/Flex';
import { Pill } from '@/components/Pill';
import { Section, SectionHeader } from '@/components/Section';
import { Select } from '@/components/Select';
import { Skeleton } from '@/components/Skeleton';
import * as sharedStyles from '@/components/surveys/styles.css';
import { color } from '@/styles/color';
import { typography } from '@/styles/typography';
import { formatNumber } from '@/utils';
import { customCanvasBackgroundColorOptions, customCanvasBackgroundColorPlugin, wrapChartLabel } from '@/utils/chart';
import * as styles from './HighLowAnalysisChart.css';

ChartJS.register(CategoryScale, LinearScale, BarController, BarElement, Title, Tooltip, Legend);

/**
 * Extra legend plugin for the high-low analysis chart.
 * Displayed above the list of questions on the left, and the bars on the right.
 */
const extraLegendPlugin: Plugin<'bar'> = {
    id: 'extraLegendPlugin',
    beforeDatasetsDraw: (chart, _args, _pluginOptions) => {
        const {
            ctx,
            chartArea: { left, right },
        } = chart;

        ctx.font = `${typography.fontWeight.bold} ${typography.fontSize.sm} ${typography.fontFamily.sans}`;
        ctx.fillStyle = color.grey[500];
        ctx.textBaseline = 'top';

        ctx.textAlign = 'right';
        ctx.fillText('設問', left - 48, 0);

        ctx.textAlign = 'left';
        ctx.fillText('肯定率と差分', left + (right - left) / 2, 0);
    },
};

type HighLowAnalysisChartProps = {
    /** `null` while still loading. */
    attributeSettings: SurveyAttribute[] | null;
    settingId: string | null;
    onSettingChange: (settingId: string | null) => void;
    /**
     * `null` until the user picks a setting, which is what triggers the fetch — so
     * unlike `attributeSettings` this staying `null` is not (only) a loading state.
     * The underlying query keeps the previous setting's data around during a
     * refetch, so this also doesn't go back to `null` on a setting change — it's
     * only ever `null` before the first fetch, or once a fetch resolves with no
     * comparable data (`isFetching` disambiguates the latter from "still loading").
     */
    scores: SurveyHighLowAnalysisScores['scores'] | null;
    /** True while `scores` is being (re)fetched for the selected setting. */
    isFetching: boolean;
    isError: boolean;
    onRetry: () => void;
};

/**
 * Prototype: floating bar chart showing, per question, the gap in favorable
 * (positive) answer rate between the options with the highest and lowest
 * favorable rate in a chosen attribute setting.
 */
export function HighLowAnalysisChart({
    attributeSettings,
    settingId,
    onSettingChange,
    scores,
    isFetching,
    isError,
    onRetry,
}: HighLowAnalysisChartProps) {
    if (isError) {
        return <ErrorOverlay active onRetry={onRetry} message="ハイロー分析 - 設問別の取得に失敗しました。" />;
    }

    return (
        <Section flex>
            <SectionHeader
                title="ハイロー分析 - 設問別"
                description="選択した属性の中から肯定率が最も高い選択肢と最も低い選択肢を抽出し、設問ごとに両者の肯定率を帯で結んで差分を示します。対象の回答者が 3 人未満の選択肢は、比較対象から除外されます。"
            />

            <Flex direction="column" gap="4">
                {attributeSettings ? (
                    <SettingSelect
                        attributeSettings={attributeSettings}
                        settingId={settingId}
                        onSettingChange={onSettingChange}
                    />
                ) : (
                    <SettingSelectSkeleton />
                )}

                {
                    scores ? (
                        // Has data — from this selection, or (via `placeholderData`) the
                        // previous one while a refetch for a new selection is in flight.
                        <Skeleton.Overlay active={isFetching}>
                            <HighLowChart scores={scores} />
                        </Skeleton.Overlay>
                    ) : isFetching ? (
                        // No data yet, and a fetch is in flight — genuinely loading.
                        <HighLowChartSkeleton />
                    ) : settingId ? (
                        // Settled with no data — the setting has fewer than 2 options
                        // with a computable favorable rate, not a loading state.
                        // TODO: Proper error component
                        <div>比較可能なデータがありません。</div>
                    ) : null // Nothing selected yet — nothing to show.
                }
            </Flex>
        </Section>
    );
}

type SettingSelectProps = {
    attributeSettings: SurveyAttribute[];
    settingId: string | null;
    onSettingChange: (settingId: string | null) => void;
};

function SettingSelect({ attributeSettings, settingId, onSettingChange }: SettingSelectProps) {
    return (
        <div>
            <Select value={settingId ?? ''} onChange={(e) => onSettingChange(e.target.value || null)}>
                <option value="" disabled>
                    --属性を選択してください--
                </option>
                {attributeSettings.map((attribute) => (
                    <option key={attribute.id} value={attribute.id}>
                        {attribute.name}
                    </option>
                ))}
            </Select>
        </div>
    );
}

function SettingSelectSkeleton() {
    return <Skeleton.Box width={220} height={42} />;
}

type HighLowChartProps = {
    scores: NonNullable<SurveyHighLowAnalysisScores['scores']>;
};

function HighLowChart({ scores }: HighLowChartProps) {
    const data: ChartData<'bar'> = useMemo(() => {
        return {
            labels: scores.questions.map((q) => wrapChartLabel(q.text, 30)),
            datasets: [
                {
                    label: `${scores.highestOption.name} - ${scores.lowestOption.name}`,
                    data: scores.questions.map((q): [number, number] => [
                        q.favorableRateForLowest,
                        q.favorableRateForHighest,
                    ]),
                    backgroundColor: scores.questions.map((q) => {
                        // TODO: Do not hardcode bar color
                        return q.delta < 0 ? color.peach[500] : '#3b82f6';
                    }),
                    borderRadius: 0,
                },
            ],
        };
    }, [scores]);

    const options: ChartOptions<'bar'> = useMemo(() => {
        return {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            layout: {
                padding: {
                    top: 38,
                },
            },
            scales: {
                x: {
                    min: 0,
                    max: 100,
                    grid: { color: '#e5e7eb' },
                    afterTickToLabelConversion(axis) {
                        axis.ticks.forEach((tick) => {
                            tick.label = `${tick.label}%`;
                        });
                    },
                },
                y: { grid: { display: false } },
            },
            plugins: {
                legend: { display: false },
                tooltip: {
                    callbacks: {
                        label: (item) => {
                            const [low, high] = item.raw as [number, number];
                            return [
                                `肯定率`,
                                `${scores.highestOption.name}: ${formatNumber(high)}%`,
                                `${scores.lowestOption.name}: ${formatNumber(low)}%`,
                                `差分: ${formatNumber(high - low)}%`,
                            ];
                        },
                    },
                },
                datalabels: {
                    labels: {
                        lowest: {
                            color: color.grey[800],
                            anchor: 'start',
                            align: 'start',
                            formatter(_value, context) {
                                const [lowest] = context.dataset.data[context.dataIndex] as [number, number];
                                // Only show the label if there's enough room
                                return lowest > 5 ? `${formatNumber(lowest)}%` : '';
                            },
                        },
                        highest: {
                            color: color.grey[800],
                            anchor: 'end',
                            align: 'end',
                            formatter(_value, context) {
                                const [, highest] = context.dataset.data[context.dataIndex] as [number, number];
                                // Only show the label if there's enough room
                                return highest < 95 ? `${formatNumber(highest)}%` : '';
                            },
                        },
                        delta: {
                            color: 'white',
                            anchor: 'center',
                            align: 'center',
                            formatter(_value, context) {
                                const [lowest, highest] = context.dataset.data[context.dataIndex] as [number, number];
                                const delta = highest - lowest;
                                // Only show the label if there's enough room
                                return delta > 5 ? `${formatNumber(delta)}%` : '';
                            },
                        },
                    },
                },
                ...customCanvasBackgroundColorOptions,
            },
        };
    }, [scores]);

    return (
        <Flex direction="column" gap="4">
            <Flex gap="4">
                <Flex flex direction="column" align="center" className={styles.pickupOptionVariants.highest}>
                    <Flex direction="column" align="center" gap="4">
                        <span>
                            肯定率が最も<em className={styles.pickupOptionNameVariants.highest}>高い</em>
                        </span>
                        <Flex gap="2">
                            <Pill weight="bold" variant="secondary">
                                {scores.highestOption.name}
                            </Pill>
                            <Pill weight="bold" variant="tertiary">
                                肯定率 {formatNumber(scores.highestOption.favorableRate)}%
                            </Pill>
                        </Flex>
                    </Flex>
                </Flex>
                <Flex flex direction="column" align="center" className={styles.pickupOptionVariants.lowest}>
                    <Flex direction="column" align="center" gap="4">
                        <span>
                            肯定率が最も<em className={styles.pickupOptionNameVariants.lowest}>低い</em>
                        </span>
                        <Flex gap="2">
                            <Pill weight="bold" variant="primary">
                                {scores.lowestOption.name}
                            </Pill>
                            <Pill weight="bold" variant="tertiary">
                                肯定率 {formatNumber(scores.lowestOption.favorableRate)}%
                            </Pill>
                        </Flex>
                    </Flex>
                </Flex>
            </Flex>

            <div className={sharedStyles.chartContainer}>
                <Bar
                    data={data}
                    options={options}
                    plugins={[customCanvasBackgroundColorPlugin, extraLegendPlugin, ChartDataLabels]}
                />
            </div>
        </Flex>
    );
}

function HighLowChartSkeleton() {
    return (
        <>
            <Flex gap="4">
                <Skeleton.Box width="100%" height={100} />
                <Skeleton.Box width="100%" height={100} />
            </Flex>
            <Skeleton.Box width="100%" height={300} />
        </>
    );
}

/**
 * A placeholder for the whole card, for the page's initial load — distinct from the
 * partial loading the component handles internally once it's on screen.
 */
export function HighLowAnalysisChartSkeleton() {
    return (
        <Section flex>
            <Flex direction="column" gap="4">
                <Flex direction="column" gap="1">
                    <Skeleton.Line width="40%" height="xl" />
                    <Skeleton.Line width="70%" height="sm" />
                </Flex>

                <SettingSelectSkeleton />

                {/**
                 * `HighLowChartSkeleton` is intentionally omitted as the
                 * chart is initially hidden when no `settingId` is selected.
                 */}
            </Flex>
        </Section>
    );
}
